import { z } from 'zod';
import { AtCapPolicy, CommsChannel } from '../enums.js';
import { IsoDateTime, Uuid } from '../primitives.js';

/**
 * Comms allowance + at-cap policy (design screen S2). Allowances are pooled per chair.
 * email + push are unmetered; sms + whatsapp draw down a monthly allowance.
 * Hard rule (FOUNDATION): we PAUSE at the cap — never auto-spend without explicit opt-in.
 * The full comms send pipeline is Phase 3; this is the settings + meter contract.
 */
export const CommsAllowance = z
  .object({
    salonId: Uuid,
    periodStart: IsoDateTime,
    periodEnd: IsoDateTime,
    chairs: z.int().min(1),
    smsAllowance: z.int().min(0),
    smsUsed: z.int().min(0),
    whatsappAllowance: z.int().min(0),
    whatsappUsed: z.int().min(0),
    atCapPolicy: AtCapPolicy,
    /** Pence saved this period by routing reminders over free channels before paid ones. */
    smartRoutingSavedPence: z.int().min(0),
  })
  .meta({ id: 'CommsAllowance' });
export type CommsAllowance = z.infer<typeof CommsAllowance>;

export const UpdateCommsPolicy = z
  .object({
    atCapPolicy: AtCapPolicy,
    /** Per-chair pooled allowance; FOUNDATION proposal is 250/chair. */
    smsPerChair: z.int().min(0).max(100000).optional(),
    whatsappPerChair: z.int().min(0).max(100000).optional(),
  })
  .meta({ id: 'UpdateCommsPolicy' });
export type UpdateCommsPolicy = z.infer<typeof UpdateCommsPolicy>;

/** A single line in the per-message audit log (so invoices are explainable). */
export const CommsLogEntry = z
  .object({
    id: Uuid,
    salonId: Uuid,
    channel: CommsChannel,
    toRedacted: z.string().describe('masked recipient, e.g. +44 ••• ••• 123'),
    segments: z.int().min(0),
    costPence: z.int().min(0),
    routedFromChannel: CommsChannel.nullable().describe('set when smart routing downgraded'),
    sentAt: IsoDateTime,
  })
  .meta({ id: 'CommsLogEntry' });
export type CommsLogEntry = z.infer<typeof CommsLogEntry>;
